import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ShoppingBag, User, Search, Menu, X, LogOut, LayoutDashboard, ChevronDown } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import api from "../lib/api";
import { motion, AnimatePresence } from "framer-motion";

// Top-level categories to show inline in nav
const FEATURED_NAV = ["maquillaje", "perfumeria", "cosmetica", "solares", "selectivo"];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [allCats, setAllCats] = useState([]);
  const [moreOpen, setMoreOpen] = useState(false);
  const { user, logout } = useAuth();
  const { count, setOpen } = useCart();
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    api.get("/categories").then(r => setAllCats(r.data || [])).catch(() => {});
  }, []);

  const featuredCats = allCats.filter(c => FEATURED_NAV.includes(c.slug));
  const otherCats = allCats.filter(c => !FEATURED_NAV.includes(c.slug));

  const submitSearch = (e) => {
    e.preventDefault();
    if (searchQ.trim()) {
      nav(`/shop?search=${encodeURIComponent(searchQ.trim())}`);
      setSearchOpen(false);
      setSearchQ("");
    }
  };

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "glass-panel" : "bg-transparent"
        }`}
      >
        <div className="bg-gold text-carbon text-center py-1.5 text-[11px] uppercase tracking-[0.25em] font-medium" data-testid="shipping-banner">
          Envío gratis a partir de 150€ · Entrega en 24-48h
        </div>
        <div className={`max-w-7xl mx-auto px-6 md:px-12 flex items-center gap-6 ${scrolled ? "py-2" : "py-4"} transition-all duration-500`}>
          {/* Left: Logo */}
          <Link
            to="/"
            data-testid="logo-link"
            className="flex-shrink-0 select-none"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_prestige-omni-store/artifacts/8poujyff_mhlumora%20logo.jpg"
              alt="M&H Lumora"
              className="h-12 md:h-14 lg:h-16 w-auto object-contain"
              style={{ mixBlendMode: "screen" }}
            />
          </Link>

          {/* Center-left: nav links (visible only desktop ≥1024px) */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-7 flex-1">
            {featuredCats.map(c => (
              <NavLink
                key={c.slug}
                to={`/shop/${c.slug}`}
                data-testid={`nav-${c.slug}`}
                className={({ isActive }) =>
                  `text-[11px] xl:text-xs uppercase tracking-[0.18em] font-light link-underline whitespace-nowrap ${isActive ? "text-gold" : ""}`
                }
              >
                {c.name}
              </NavLink>
            ))}
            {otherCats.length > 0 && (
              <div className="relative" onMouseLeave={() => setMoreOpen(false)}>
                <button
                  onMouseEnter={() => setMoreOpen(true)}
                  onClick={() => setMoreOpen(o => !o)}
                  data-testid="nav-more"
                  className="text-[11px] xl:text-xs uppercase tracking-[0.18em] font-light flex items-center gap-1 link-underline"
                >
                  Más <ChevronDown size={12} />
                </button>
                <AnimatePresence>
                  {moreOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
                      className="absolute top-full left-0 mt-4 glass-panel border border-smoke min-w-[220px] py-2 z-50"
                    >
                      {otherCats.map(c => (
                        <Link
                          key={c.slug}
                          to={`/shop/${c.slug}`}
                          onClick={() => setMoreOpen(false)}
                          className="block px-5 py-2 text-xs uppercase tracking-[0.15em] text-ivory hover:text-gold hover:bg-graphite/40"
                        >
                          {c.name}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
            <NavLink
              to="/blog"
              data-testid="nav-editorial"
              className={({ isActive }) =>
                `text-[11px] xl:text-xs uppercase tracking-[0.18em] font-light link-underline whitespace-nowrap ${isActive ? "text-gold" : ""}`
              }
            >Editorial</NavLink>
          </nav>

          {/* Spacer pushes actions to the right on mobile/tablet */}
          <div className="flex-1 lg:hidden" />

          {/* Right: actions */}
          <div className="flex items-center gap-4 md:gap-5 flex-shrink-0">
            <button
              data-testid="search-button"
              onClick={() => setSearchOpen(true)}
              className="text-ivory hover:text-gold transition-colors"
            >
              <Search size={18} />
            </button>
            {user ? (
              <div className="hidden lg:flex items-center gap-4">
                {user.role === "admin" && (
                  <Link to="/admin" data-testid="nav-admin" className="text-ivory hover:text-gold transition-colors" title="Admin">
                    <LayoutDashboard size={18} />
                  </Link>
                )}
                <Link to="/account" data-testid="nav-account" className="text-ivory hover:text-gold transition-colors">
                  <User size={18} />
                </Link>
                <button onClick={logout} data-testid="logout-btn" className="text-ivory hover:text-gold transition-colors" title="Cerrar sesión">
                  <LogOut size={18} />
                </button>
              </div>
            ) : (
              <Link to="/login" data-testid="nav-login" className="hidden lg:inline-block text-ivory hover:text-gold transition-colors">
                <User size={18} />
              </Link>
            )}
            <button
              data-testid="cart-button"
              onClick={() => setOpen(true)}
              className="relative text-ivory hover:text-gold transition-colors"
            >
              <ShoppingBag size={18} />
              {count > 0 && (
                <span className="absolute -top-2 -right-2 bg-gold text-carbon text-[10px] font-medium w-4 h-4 flex items-center justify-center rounded-full">
                  {count}
                </span>
              )}
            </button>
            {/* Hamburger - visible on mobile + tablet (< lg) */}
            <button
              data-testid="menu-toggle"
              className="lg:hidden text-ivory hover:text-gold transition-colors"
              onClick={() => setMenuOpen(true)}
              aria-label="Abrir menú"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-[60] bg-carbon"
          >
            <div className="p-6 flex justify-end">
              <button data-testid="menu-close" onClick={() => setMenuOpen(false)} className="text-ivory">
                <X size={24} />
              </button>
            </div>
            <nav className="px-8 flex flex-col gap-6">
              {allCats.map(c => (
                <Link key={c.slug} to={`/shop/${c.slug}`} onClick={() => setMenuOpen(false)}
                  className="font-heading text-3xl text-ivory hover:text-gold">
                  {c.name}
                </Link>
              ))}
              <Link to="/blog" onClick={() => setMenuOpen(false)} className="font-heading text-3xl text-ivory hover:text-gold">
                Editorial
              </Link>
              <div className="border-t border-smoke pt-6 mt-4 flex flex-col gap-4">
                {user ? (
                  <>
                    <Link to="/account" onClick={() => setMenuOpen(false)} className="text-ivory uppercase text-xs tracking-[0.2em]">Mi cuenta</Link>
                    {user.role === "admin" && (
                      <Link to="/admin" onClick={() => setMenuOpen(false)} className="text-gold uppercase text-xs tracking-[0.2em]">Panel admin</Link>
                    )}
                    <button onClick={() => { logout(); setMenuOpen(false); }} className="text-ivory uppercase text-xs tracking-[0.2em] text-left">Cerrar sesión</button>
                  </>
                ) : (
                  <Link to="/login" onClick={() => setMenuOpen(false)} className="text-ivory uppercase text-xs tracking-[0.2em]">Iniciar sesión</Link>
                )}
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Search overlay */}
      <AnimatePresence>
        {searchOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] glass-panel flex items-start justify-center pt-32 px-6"
            onClick={() => setSearchOpen(false)}
          >
            <form onSubmit={submitSearch} className="w-full max-w-2xl" onClick={e => e.stopPropagation()}>
              <p className="overline mb-4">Buscar</p>
              <input
                data-testid="search-input"
                autoFocus
                value={searchQ}
                onChange={e => setSearchQ(e.target.value)}
                placeholder="Diamantes, peonías, perfume oud..."
                className="w-full bg-transparent border-b border-gold text-3xl md:text-5xl font-heading text-ivory placeholder:text-ivory/30 py-4 outline-none"
              />
              <p className="text-xs text-ivory/40 mt-3 tracking-wider">Pulsa ENTER para buscar · ESC para cerrar</p>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
